// @flow
import { has } from "../utils/helpers/objects";
import Locales from "../i18n/Locales";

import resolveModel from "../models/resolveModel";

import ModularUIResponse from "../modularui/ModularUIResponse";

/**
 * Recreate a model from dehydrated data obtained after a server render.
 */
const recreateModel = (data: Object) => {
  const modelData = ModularUIResponse.rehydrate(data);

  const Model = resolveModel(modelData);

  if (Model) {
    const model = new Model(modelData);
    model.rehydrate(data);

    const childModels = data.childModels.map((childModel) =>
      recreateModel(childModel),
    );

    model.addChildModels(childModels, []);

    return model;
  }

  return data;
};

/**
 */
const isModularUIModelData = (data: any) =>
  data != null &&
  typeof data === "object" &&
  has(data, "data") &&
  has(data, "contributions");

/**
 */
const rehydratedValue = (stateKey: string, stateValue: any) => {
  if (Array.isArray(stateValue)) {
    return stateValue.map((stateItem) => rehydrate(stateItem)); // NOSONAR
  }

  if (isModularUIModelData(stateValue)) {
    return recreateModel(stateValue);
  }

  if (
    stateKey === "i18n" &&
    has(stateValue, "locales") &&
    has(stateValue, "locale")
  ) {
    return {
      locales: Locales.rehydrate(stateValue.locales),
      locale: stateValue?.locale,
    };
  }

  const noFurtherRehydration = [
    "preferences",
    "router",
    "progressindicator",
    "modelcatalog",
  ];
  if (noFurtherRehydration.includes(stateKey)) {
    return stateValue;
  }

  if (stateValue !== null && typeof stateValue === "object") {
    return rehydrate(stateValue); // NOSONAR
  }

  return stateValue;
};

/**
 * Maps dehydrated state to models that can be used to rehydrated the application.
 */
const rehydrate = (state: Object): { ... } => {
  const mappedState: { [string]: any } = {};

  Object.keys(state).forEach((stateKey) => {
    const stateValue = state[stateKey];
    mappedState[stateKey] = rehydratedValue(stateKey, stateValue);
  });

  return mappedState;
};

export default rehydrate;
